import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';

import { languageSupport, languageService } from '@app/modules/common/language';
import { WSConnection } from '@app/modules/sockets';

interface DispRow {
    fv: number;          // valor facial (céntimos)
    label: string;       // "5.00", "0.50"…
    dispensable: number; // piezas dispensables (recyclable)
    total: number;       // piezas totales en máquina
    amount: number;      // importe de la denominación (euros, por el total)
    devid: number;       // 1 = billete, 2 = moneda
}

interface CollectRow {
    fv: number;          // valor facial (céntimos)
    label: string;       // "5.00"
    dispensable: number; // máximo que se puede sacar
    devid: number;       // 1 = billete, 2 = moneda
    request: number;     // piezas que el operario quiere sacar
}

/**
 * "Backoffice" del cajón CashGuard (Keyser): panel de administración igual que
 * Glory/Prosegur, pero más simple — CashGuard solo tiene inventario (CONTENTS) y
 * retirada por IMPORTE (CASH_OUT), sin stacker/bolsa ni cargar/cambio.
 *
 * Vistas:
 *  - `menu`     → inventario dispensable (del `contents`) + acciones: Sacar
 *                 efectivo, Entrada/Salida, Actualizar.
 *  - `dispense` → "sacar efectivo": el operario elige piezas por denominación; se
 *                 suma el importe y se manda un CASH_OUT (CashGuard dispensa por
 *                 importe, no por desglose exacto).
 *
 * Carga el inventario con el comando **CONTENTS** (el mismo que usa el arqueo de
 * Entrada/Salida y que sí responde de forma fiable). El CashGuard RestApi de
 * StrongPoint no tiene un endpoint "backoffice", así que el PIN se valida en el
 * front contra el pin configurado del cajón (`drawer.backofficePin`, por defecto
 * 1515) — no hace falta round-trip al backend para el gate.
 */
@Component({
    selector: 'ui-modal-cashguard-backoffice',
    templateUrl: './ui-cashguard-backoffice.html',
    styleUrls: [ './ui-cashguard-backoffice.scss' ]
})
export class UiCashGuardBackofficeModal extends languageSupport implements OnInit, OnDestroy {
    public _connection: WSConnection = null;
    public _target: string = 'CASHGUARD';
    private _pin: string = '1515';

    public status: 'pin' | 'loading' | 'ready' | 'error' = 'pin';
    public view: 'menu' | 'dispense' = 'menu';
    public pinInput: string = '';
    public pinError: boolean = false;
    public errorMessage: string = null;

    public notes: DispRow[] = [];
    public coins: DispRow[] = [];
    public totalDispensable: number = 0;   // euros (lo que la máquina puede entregar)
    public grandTotal: number = 0;         // euros (total en máquina)

    public busy: boolean = false;
    public actionMsg: string = null;
    public actionOk: boolean = false;
    public resetArmed: boolean = false;   // 1er tap arma, 2º confirma el RESET
    private _resetTimer: any = null;

    // Sacar efectivo (selector por denominación).
    public collectClass: 'notes' | 'coins' | 'both' = 'both';
    public collectRows: CollectRow[] = [];

    // Callback del padre (ui-tab-cash) para REGISTRAR en el arqueo la retirada
    // (Sacar efectivo -> salida). CashGuard aquí solo retira (no carga cambio).
    private _recordMovement: ((eur: number, type: 'withdrawal' | 'load') => void) | null = null;

    constructor(private lang: languageService, private change: ChangeDetectorRef, private modalCtrl: ModalController, private params: NavParams) {
        super(lang, change);
        this._connection = this.params.get('connection');
        this._target = this.params.get('target') || 'CASHGUARD';
        const _p = this.params.get('pin');
        if (_p != null && String(_p).length > 0){
            this._pin = String(_p);
        }
        this._recordMovement = this.params.get('recordMovement') || null;
    }

    // Inventario DISPENSABLE por denominación en céntimos { fv: piezas } para que
    // el arqueo Entrada/Salida no vuelva a pedir CONTENTS (#2).
    private _inventoryMap(): { [fv: number]: number } {
        let _out: { [fv: number]: number } = {};
        for (let _r of [...(this.notes || []), ...(this.coins || [])]){
            if (_r && _r.fv > 0 && _r.dispensable > 0){
                _out[_r.fv] = (_out[_r.fv] || 0) + _r.dispensable;
            }
        }
        return _out;
    }

    ngOnInit() {
        this.status = 'pin';
    }

    ngOnDestroy() {
        if (this._resetTimer){ clearTimeout(this._resetTimer); this._resetTimer = null; }
        super.OnDestroy();
    }

    /*****************************/
    /* PIN (validación backend)  */
    /*****************************/
    OnPinKey(digit: string) {
        if (this.pinInput.length >= 8){ return; }
        this.pinInput += digit;
        this.pinError = false;
        this.change.markForCheck();
    }
    OnPinBack() {
        this.pinInput = this.pinInput.slice(0, -1);
        this.pinError = false;
        this.change.markForCheck();
    }
    async OnPinSubmit() {
        // CONTENTS no tiene gate de PIN en el RestApi, así que validamos en el
        // front contra el pin configurado del cajón (`_pin`, de drawer.backofficePin
        // o 1515 por defecto). Si no cuadra, volvemos a pedirlo.
        if (!this.pinInput || this.pinInput.length === 0){ return; }
        if (this.pinInput !== this._pin){
            this.pinError = true;
            this.pinInput = '';
            this.change.markForCheck();
            return;
        }
        await this._load();
    }

    OnClose() {
        // No cerrar con una operación en curso (dispense) NI mientras carga
        // (STATUS/BACKOFFICE en vuelo): cerrar desconectaría la conexión compartida
        // dejando el comando sin respuesta.
        if (this.busy || this.status === 'loading'){ return; }
        this.modalCtrl.dismiss();
    }

    // "Entrada/Salida de efectivo" (arqueo): la rejilla vive en ui-tab-cash, así
    // que cerramos el backoffice señalando al control de caja que la abra.
    OnCashInOut() {
        if (this.busy){ return; }
        // Inventario para el arqueo (#2) SOLO si la carga fue OK ('ready'); si
        // falló, lo omitimos para que el arqueo re-lea (no ceros/stale).
        let _inv = (this.status === 'ready') ? this._inventoryMap() : null;
        this.modalCtrl.dismiss({ action: 'cashinout', inventory: _inv });
    }

    async OnRefresh() {
        if (this.busy){ return; }
        await this._load();
    }

    /*****************************/
    /* RESET DISPOSITIVO         */
    /*****************************/
    // Reset del cajón (RestApi `/cashGuard?action=reset`): recuperación tras error
    // ya resuelto físicamente. El backend cancela la cola pendiente antes. Doble
    // tap para evitar resets accidentales (cancela transacciones en cola).
    async OnReset() {
        if (this.busy){ return; }
        if (!this.resetArmed){
            this.resetArmed = true;
            this.actionMsg = null;
            if (this._resetTimer){ clearTimeout(this._resetTimer); }
            this._resetTimer = setTimeout(() => { this.resetArmed = false; this.change.markForCheck(); }, 4000);
            this.change.markForCheck();
            return;
        }
        this.resetArmed = false;
        if (this._resetTimer){ clearTimeout(this._resetTimer); this._resetTimer = null; }
        await this._session('RESET', '@cashguard_reset_success', '@cashguard_reset_error');
    }

    /*****************************/
    /* SESIÓN (LOGIN / LOGOUT)   */
    /*****************************/
    // Iniciar sesión en el cajón (misma lógica que el toggle del control de caja:
    // comando LOGIN). Tras iniciar, refresca el inventario.
    async OnLogin() {
        await this._session('LOGIN', '@cashguard_login_success', '@cashguard_login_error');
    }
    // Finalizar sesión (comando LOGOUT).
    async OnLogout() {
        await this._session('LOGOUT', '@cashguard_logout_success', '@cashguard_logout_error');
    }

    private async _session(command: string, okKey: string, errKey: string) {
        if (this.busy || !this._connection){ return; }
        this.busy = true;
        this.actionMsg = null;
        this.change.markForCheck();
        try {
            let _resp = await Promise.race([
                this._connection.Send(this._target, { command: command, pin: this._pin }),
                new Promise<null>(resolve => setTimeout(() => resolve(null), 15000))
            ]);
            let _ok = !!(_resp && _resp.exec === 'SUCCESS');
            this.actionOk = _ok;
            this.actionMsg = this.tr(_ok ? okKey : errKey);
        } catch (e) {
            console.error('[CASHGUARD][backoffice] session ' + command + ' failed', e);
            this.actionOk = false;
            this.actionMsg = this.tr(errKey);
        }
        this.busy = false;
        this.change.markForCheck();
        // LOGIN abre la sesión → refrescamos el inventario. LOGOUT la cierra → el
        // inventario ya no aplica, pero un _load reevalúa el estado.
        await this._load();
    }

    /*****************************/
    /* SACAR EFECTIVO            */
    /*****************************/
    OnOpenDispense() {
        this.actionMsg = null;
        this.setCollectClass('both');
        this.view = 'dispense';
        this.change.markForCheck();
    }
    OnBackToMenu() {
        this.view = 'menu';
        this.actionMsg = null;
        this.change.markForCheck();
    }

    // Construye las filas del selector con el DISPENSABLE de la clase. Muestra
    // TODAS las denominaciones aunque estén a 0 (stepper deshabilitado con 0).
    setCollectClass(k: 'notes' | 'coins' | 'both') {
        this.collectClass = k;
        const src: DispRow[] = k === 'notes' ? this.notes
            : k === 'coins' ? this.coins
            : [...this.notes, ...this.coins];
        this.collectRows = src
            .map(r => ({ fv: r.fv, label: r.label, dispensable: r.dispensable, devid: r.devid, request: 0 }));
        this.change.markForCheck();
    }

    incRequest(row: CollectRow) {
        if (row.request < row.dispensable){ row.request++; this.change.markForCheck(); }
    }
    decRequest(row: CollectRow) {
        if (row.request > 0){ row.request--; this.change.markForCheck(); }
    }
    setMaxRequest(row: CollectRow) {
        row.request = row.dispensable; this.change.markForCheck();
    }

    get CollectPieces(): number { return this.collectRows.reduce((a, r) => a + r.request, 0); }
    get CollectAmount(): number { return this.collectRows.reduce((a, r) => a + r.request * r.fv, 0) / 100; }
    get CanCollect(): boolean { return !this.busy && this.CollectPieces > 0; }

    // Sacar: se suma el importe de las piezas elegidas y se manda un CASH_OUT
    // (CashGuard dispensa por IMPORTE; la máquina elige el desglose). El selector
    // por pieza sirve para componer el importe.
    async OnCollectStart() {
        if (!this.CanCollect){ return; }
        let _cents = this.collectRows.reduce((a, r) => a + r.request * r.fv, 0);
        await this._cashOut(_cents);
    }

    // "Sacar todo": todo el dispensable de la clase seleccionada.
    async OnCollectAll() {
        const src: DispRow[] = this.collectClass === 'notes' ? this.notes
            : this.collectClass === 'coins' ? this.coins
            : [...this.notes, ...this.coins];
        let _cents = src.reduce((a, r) => a + r.dispensable * r.fv, 0);
        if (_cents <= 0){ return; }
        await this._cashOut(_cents);
    }

    // CASH_OUT por importe (céntimos) y refresca. Lee el resultado del cajón.
    private async _cashOut(amountCents: number) {
        if (this.busy || !this._connection || amountCents <= 0){ return; }
        this.busy = true;
        this.actionMsg = null;
        this.change.markForCheck();
        try {
            let _txId = Math.floor(Math.random() * 1000000);
            let _resp = await this._connection.Send(this._target, {
                command: 'CASH_OUT',
                pin: this._pin,
                transaction_id: _txId,
                amount_due: amountCents
            });
            let _body = this._body(_resp);
            let _ok = !!(_resp && _resp.exec === 'SUCCESS' && (!_body || _body['status'] !== 'error'));
            let _amt = ' · ' + (amountCents / 100).toFixed(2) + this.tr('@currency');
            if (_ok){
                this.actionOk = true;
                // Sacar efectivo = SALIDA: registrar en el arqueo el importe retirado.
                this._recordMovement?.(amountCents / 100, 'withdrawal');
                this.actionMsg = this.tr('@cashguard_cashout_success') + _amt;
            } else {
                this.actionOk = false;
                this.actionMsg = this.tr('@cashguard_insufficient_cash');
            }
        } catch (e) {
            console.error('[CASHGUARD][backoffice] cash-out failed', e);
            this.actionOk = false;
            this.actionMsg = this.tr('@cashguard_cashout_error');
        }
        this.busy = false;
        this.change.markForCheck();
        await this._load();
        this.setCollectClass(this.collectClass);   // reconstruye filas con el nuevo inventario
    }

    /*****************************/
    /* INVENTARIO (CONTENTS)     */
    /*****************************/
    private async _load() {
        this.status = 'loading';
        this.change.markForCheck();
        try {
            if (!this._connection){
                this._fail(this.tr('@cashguard_connection_failed'));
                return;
            }
            // Warm-up de sesión (STATUS) antes del inventario, como el control de caja.
            try {
                await Promise.race([
                    this._connection.Send(this._target, { command: 'STATUS' }),
                    new Promise<null>(resolve => setTimeout(() => resolve(null), 12000))
                ]);
            } catch(_){}

            // CONTENTS: el inventario NO viene en la respuesta correlacionada del
            // Send (esa trae data="Contents retrieved successfully", un string),
            // sino en un frame BROADCAST aparte ({command:'CONTENTS', contents})
            // que emite el Rust. Lo capturamos por OnMessageRecv (llega por LAN) y,
            // como fallback (WSR), parseamos el data del envelope por si viniera
            // como JSON. MISMO patrón que el arqueo Entrada/Salida, que sí lee bien.
            let _contents: any = null;
            let _sub = this._connection.OnMessageRecv.subscribe((msg: any) => {
                if (_contents){ return; }
                if (msg && msg.name === this._target){
                    let _d = msg.data;
                    if (_d && typeof _d === 'object' && _d.command === 'CONTENTS' && _d.contents){
                        _contents = _d;
                    } else if (typeof _d === 'string'){
                        try {
                            let _p = JSON.parse(_d);
                            if (_p && _p.command === 'CONTENTS' && _p.contents){ _contents = _p; }
                        } catch(_){}
                    }
                }
            });
            let _resp: any;
            try {
                _resp = await Promise.race([
                    this._connection.Send(this._target, { command: 'CONTENTS' }),
                    new Promise<null>(resolve => setTimeout(() => resolve(null), 25000))
                ]);
            } finally {
                if (_sub){ _sub.unsubscribe(); }
            }
            // Fallback: el envelope trae el contents como JSON (algunos transportes).
            if (!_contents){
                let _body = this._body(_resp);
                if (_body && _body['contents']){ _contents = _body; }
            }
            if (!_contents){
                this._fail(_resp && _resp['data'] && _resp['data']['message']);
                return;
            }
            this._fill(_contents);
            this.status = 'ready';
        } catch (e) {
            console.error('[CASHGUARD][backoffice] load failed', e);
            this._fail();
        }
        this.change.markForCheck();
    }

    // Parsea el `contents` del CashGuard: array de bloques con `denominations`
    // [{ denomination (céntimos), recyclable (dispensable), deposited (en stacker) }].
    // El total en máquina de cada denominación = recyclable + deposited: el efectivo
    // del compartimento de depósito NO es dispensable, pero SÍ está en la máquina y
    // debe contar en el total/importe/grandTotal (si no, "Total en máquina" saldría
    // igual que "Dispensable"). Shape confirmado en client_cashguard.py.
    private _fill(body: any) {
        let _contents = body && body['contents'];
        let _arr = Array.isArray(_contents) ? _contents
                 : (_contents && Array.isArray(_contents.contents) ? _contents.contents : null);
        let _acc: { [fv: number]: { disp: number, total: number } } = {};
        for (let _entry of (_arr || [])){
            for (let _d of (_entry.denominations || [])){
                let _fv = this._num(_d, 'denomination', 0);       // YA en céntimos
                if (_fv <= 0){ continue; }
                let _disp = this._num(_d, 'recyclable', 0);
                // Depositado (stacker/caja): no dispensable pero cuenta en el total.
                // Compat: si algún backend enviara `count`/`total` como total directo,
                // se respeta ese valor (máx con el dispensable) en vez de sumar.
                let _dep = this._num(_d, 'deposited', 0);
                let _explicitTotal = this._num(_d, 'count', this._num(_d, 'total', -1));
                let _total = _explicitTotal >= 0 ? Math.max(_explicitTotal, _disp) : (_disp + _dep);
                if (!_acc[_fv]){ _acc[_fv] = { disp: 0, total: 0 }; }
                _acc[_fv].disp += _disp;
                _acc[_fv].total += _total;
            }
        }
        let rows: DispRow[] = Object.keys(_acc).map(k => {
            let _fv = Number(k);
            let _disp = _acc[_fv].disp;
            let _total = Math.max(_acc[_fv].total, _disp);
            return {
                fv: _fv,
                label: (_fv / 100).toFixed(2),
                dispensable: _disp,
                total: _total,
                amount: (_fv * _total) / 100,
                devid: _fv >= 500 ? 1 : 2
            } as DispRow;
        }).sort((a, b) => a.fv - b.fv);

        this.notes = rows.filter(r => r.fv >= 500);
        this.coins = rows.filter(r => r.fv < 500);
        this.totalDispensable = rows.reduce((a, r) => a + r.fv * r.dispensable, 0) / 100;
        this.grandTotal = rows.reduce((a, r) => a + r.fv * r.total, 0) / 100;
    }

    ColDisp(rows: DispRow[]): number { return rows.reduce((a, r) => a + r.dispensable, 0); }
    ColTotal(rows: DispRow[]): number { return rows.reduce((a, r) => a + r.total, 0); }
    ColAmount(rows: DispRow[]): number { return rows.reduce((a, r) => a + r.amount, 0); }
    // Importe (€) dispensable: fv (céntimos) × piezas dispensables. Para la línea de
    // total por columna del pie (cuánto dinero es dispensable, no solo el total).
    ColDispAmount(rows: DispRow[]): number { return rows.reduce((a, r) => a + r.fv * r.dispensable, 0) / 100; }

    /*****************************/
    /* HELPERS                   */
    /*****************************/
    private _body(resp: any): any {
        let data = resp && resp['data'];
        if (typeof data === 'string'){
            try { data = JSON.parse(data); } catch (_) { return null; }
        }
        return (data && typeof data === 'object') ? data : null;
    }

    private _num(obj: any, key: string, def: number): number {
        const v = obj && obj[key];
        return (typeof v === 'number') ? v : (v !== undefined && v !== null && !isNaN(Number(v)) ? Number(v) : def);
    }

    private _fail(message?: string) {
        this.status = 'error';
        this.errorMessage = message || this.tr('@prosegur_backoffice_error');
        this.change.markForCheck();
    }
}
